import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Egg, Search } from "lucide-react";
import { api } from "../lib/api";
import { PROVINCES } from "../lib/constants";
import { useLanguage } from "../context/LanguageContext";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Pagination } from "../components/Pagination";
import { SPECIES_FR } from "../components/litters/LitterCard";
import { LitterGrid } from "../components/litters/LitterGrid";
import { LitterAlertButton } from "../components/litters/LitterAlertButton";
import { useAuth } from "../context/AuthContext";


const Pick = ({ value, onChange, items, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="h-11 rounded-xl bg-white"><SelectValue /></SelectTrigger>
    <SelectContent className="bg-white">{items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

export default function Litters() {
  const { lang } = useLanguage();
  const fr = lang === "fr";
  const [params, setParams] = useSearchParams();
  const [species, setSpecies] = useState([]);
  const [res, setRes] = useState(null);
  const [breed, setBreed] = useState(params.get("breed") || "");
  const [typed, setTyped] = useState(false);
  const [tick, setTick] = useState(0);
  const { user } = useAuth();
  const urlBreed = params.get("breed") || "";
  const get = (k, d = "all") => params.get(k) || d;
  const page = parseInt(params.get("page") || "1", 10) || 1;
  const set = (k, v) => { const n = new URLSearchParams(params); v && v !== "all" ? n.set(k, v) : n.delete(k); n.delete("page"); setParams(n); };

  useEffect(() => { api.get("/litters/species").then((r) => setSpecies(r.data.species)).catch(() => {}); }, []);
  useEffect(() => { setBreed(urlBreed); }, [urlBreed]);
  useEffect(() => {
    if (!typed) return undefined;
    const h = setTimeout(() => { setTyped(false); if (breed.trim() !== urlBreed) set("breed", breed.trim()); }, 400);
    return () => clearTimeout(h);
  }, [breed, typed]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const q = new URLSearchParams({ page: String(page) });
    ["species", "province", "status", "breed"].forEach((k) => params.get(k) && q.set(k, params.get(k)));
    api.get(`/litters/browse?${q}`).then((r) => setRes(r.data)).catch(() => setRes({ items: [], total: 0, pages: 1, page: 1 }));
  }, [params, page, tick, user?.user_id]);
  useEffect(() => { setRes(null); }, [params]);

  const goPage = (p) => { const n = new URLSearchParams(params); p > 1 ? n.set("page", String(p)) : n.delete("page"); setParams(n); window.scrollTo({ top: 0, behavior: "smooth" }); };
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="litters-page">
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-[#12234A] flex items-center gap-3"><Egg className="text-[#F5551E]" size={38} /> {fr ? "Portées à venir" : "Upcoming Litters"}</h1>
      <p className="text-slate-500 mt-2 max-w-2xl text-base">{fr ? "Découvrez les portées et couvées prévues partout au Canada et inscrivez-vous sur la liste d'attente de l'éleveur." : "Browse planned and new litters and clutches across Canada, then join the breeder's waitlist to be first in line."}</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-8" data-testid="litters-filters">
        <div className="relative">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={breed} onChange={(e) => { setBreed(e.target.value); setTyped(true); }} placeholder={fr ? "Race, ex. golden" : "Breed, e.g. golden"} data-testid="litters-breed-input" className="h-11 pl-10 rounded-xl bg-white" />
        </div>
        <Pick value={get("species")} onChange={(v) => set("species", v)} testid="litters-species-select"
          items={[["all", fr ? "Toutes les espèces" : "All species"], ...species.map((s) => [s, fr ? SPECIES_FR[s] || s : s])]} />
        <Pick value={get("province")} onChange={(v) => set("province", v)} testid="litters-province-select" items={PROVINCES.map((p) => [p.code, p.name])} />
        <Pick value={get("status")} onChange={(v) => set("status", v)} testid="litters-status-select"
          items={[["all", fr ? "À venir et nés" : "Upcoming & arrived"], ["upcoming", fr ? "À venir seulement" : "Upcoming only"], ["arrived", fr ? "Déjà nés" : "Already arrived"]]} />
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-4"><LitterAlertButton params={params} fr={fr} />
        <Link to="/litter-journey" data-testid="litters-journey-link" className="text-sm font-semibold text-[#F5551E] hover:underline">{fr ? "Comment fonctionnent les portées, étape par étape →" : "How litters work, step by step →"}</Link></div>
      <p className="text-sm text-slate-500 mt-6" data-testid="litters-total">{res ? `${res.total} ${fr ? "résultat(s)" : res.total === 1 ? "litter" : "litters"}` : "\u00a0"}</p>
      {!res ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-3">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-72 rounded-2xl" />)}</div>
      ) : res.items.length ? (
        <LitterGrid items={res.items} onChanged={() => setTick((n) => n + 1)} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-3" />
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200/80 p-12 text-center text-slate-500 mt-3" data-testid="litters-empty">{fr ? "Aucune portée ne correspond pour l'instant. Essayez d'élargir votre recherche." : "No litters match yet. Try widening your search."}</div>
      )}
      {res && <Pagination page={res.page} pages={res.pages} onChange={goPage} prevLabel={fr ? "Préc." : "Prev"} nextLabel={fr ? "Suiv." : "Next"} />}
    </div>
  );
}
